import { z } from 'zod';
import { secureImageUrl } from '@/utils/url/secureImageUrl';

export * from './holidays';

/**
 * NBC API Response Schemas
 *
 * Schemas for the NBC (Non Bookable Content) REST API.
 * NBC provides supplementary product information that enriches Player API data.
 *
 * Follows Zod-first pattern:
 * - Schemas define runtime validation
 * - TypeScript types inferred from schemas
 * - Single source of truth (no type duplication)
 *
 * @module types/schemas/nbc
 */

// ============================================================================
// Common Schemas
// ============================================================================

/**
 * Tag schema - used across both endpoints
 *
 * Tags provide metadata labels for products (e.g., "Pool", "WiFi", "Spa").
 */
export const NBCTagSchema = z.object({
  label: z.string(),
});

export type NBCTag = z.infer<typeof NBCTagSchema>;

/**
 * Product type enumeration
 *
 * Defines the type of product in the NBC system.
 */
export const ProductTypeSchema = z.enum(['hotel', 'roundtrip']);

export type ProductType = z.infer<typeof ProductTypeSchema>;

// ============================================================================
// Single Product Endpoint Schemas
// ============================================================================

/**
 * Room type image schema
 *
 * Contains thumbnail and large version URLs with optional caption.
 */
export const RoomTypeImageSchema = z.object({
  thumbnail: z.string().url().transform(secureImageUrl),
  large: z.string().url().transform(secureImageUrl),
  caption: z.string().nullable(),
});

export type RoomTypeImage = z.infer<typeof RoomTypeImageSchema>;

/**
 * Room type schema
 *
 * Defines a specific room type within a hotel product.
 * Includes room code, name, images, and HTML description.
 */
export const RoomTypeSchema = z.object({
  roomTypeCode: z.string(),
  roomTypeName: z.string(),
  roomTypeImages: z.array(RoomTypeImageSchema),
  roomTypeDescription: z.string().nullable(), // HTML content
});

export type RoomType = z.infer<typeof RoomTypeSchema>;

/**
 * Single product response schema
 *
 * Used on offer page for detailed product information.
 * Contains complete product details including room types, images,
 * tags, and HTML content sections.
 *
 * @example
 * GET /de/nbc/product/{productId}
 */
export const NBCSingleProductResponseSchema = z.object({
  productId: z.string(),
  name: z.string().nullable(),
  productType: ProductTypeSchema,
  isPackage: z.boolean().optional(), // NBC backend: produkt je Pauschalreise (fallback ?? false ve store)
  mainImage: z.string().url().transform(secureImageUrl).nullable(),
  destinationName: z.string().nullable(),
  pdpUrl: z.string().url().nullable(),
  highlights: z.string().nullable(), // HTML content
  description: z.string().nullable(), // HTML content
  service: z.string().nullable(), // HTML content
  terms: z.string().nullable(), // HTML content
  providerTermsLink: z.string().nullish(), // Third-party provider GTC link (NBC backend)
  cancellationTerms: z.string().nullish(), // Cancellation terms link (NBC backend; may be relative path)
  entryInfo: z.string().nullable(), // HTML content
  tags: z.array(NBCTagSchema),
  roomTypes: z.array(RoomTypeSchema),
});

export type NBCSingleProductResponse = z.infer<typeof NBCSingleProductResponseSchema>;

// ============================================================================
// Product List Endpoint Schemas
// ============================================================================

/**
 * Product list item schema
 *
 * Teaser data for product list page.
 * Contains essential information for product cards/teasers.
 *
 * @example
 * GET /de/nbc/product-list?ids=123,456,789
 */
export const NBCProductListItemSchema = z.object({
  productId: z.string(),
  name: z.string().nullable(),
  productType: ProductTypeSchema,
  destinationName: z.string().nullable(),
  teaserHeadline: z.string().nullable(),
  teaserText: z.string().nullable(),
  teaserImages: z.array(z.string().url().transform(secureImageUrl)),
  pdpUrl: z.string().url().nullable(),
  tags: z.array(NBCTagSchema),
});

export type NBCProductListItem = z.infer<typeof NBCProductListItemSchema>;

/**
 * Product list response schema
 *
 * Wrapper response for product list endpoint.
 * Returns only products that have NBC data available.
 */
export const NBCProductListResponseSchema = z.object({
  products: z.array(NBCProductListItemSchema),
});

export type NBCProductListResponse = z.infer<typeof NBCProductListResponseSchema>;

// ============================================================================
// Type Guards (Zod-powered)
// ============================================================================

/**
 * Check if product has room type data
 *
 * @param product - NBC single product response
 * @returns True if product has at least one room type
 *
 * @example
 * const product = await getProduct('3123123');
 * if (hasRoomTypes(product)) {
 *   console.log('Product has room types:', product.roomTypes);
 * }
 */
export function hasRoomTypes(product: NBCSingleProductResponse): boolean {
  return product.roomTypes.length > 0;
}

/**
 * Check if product has teaser images
 *
 * @param product - NBC product list item
 * @returns True if product has at least one teaser image
 *
 * @example
 * const products = await getProductList(['123', '456']);
 * const withImages = products.products.filter(hasTeaserImages);
 */
export function hasTeaserImages(product: NBCProductListItem): boolean {
  return product.teaserImages.length > 0;
}

/**
 * Check if product has tags
 *
 * @param product - NBC single product or product list item
 * @returns True if product has at least one tag
 *
 * @example
 * if (hasTags(product)) {
 *   product.tags.forEach(tag => console.log(tag.label));
 * }
 */
export function hasTags(product: NBCSingleProductResponse | NBCProductListItem): boolean {
  return product.tags.length > 0;
}

/**
 * Extract room type by code
 *
 * @param product - NBC single product response
 * @param roomCode - Room type code to find
 * @returns Room type if found, undefined otherwise
 *
 * @example
 * const product = await getProduct('3123123');
 * const roomType = getRoomTypeByCode(product, 'A1');
 * if (roomType) {
 *   console.log(`Room: ${roomType.roomTypeName}`);
 *   console.log(`Images: ${roomType.roomTypeImages.length}`);
 * }
 */
export function getRoomTypeByCode(
  product: NBCSingleProductResponse,
  roomCode: string
): RoomType | undefined {
  return product.roomTypes.find((rt) => rt.roomTypeCode === roomCode);
}
